import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Code, Search, Palette, Server, Sparkles, Eye, History,
  Loader2, ChevronRight, ChevronLeft, Check, Download,
  Upload, FileCode, FileJson, Globe, Trash2, ExternalLink,
  RefreshCw, AlertCircle, FolderOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: "domain", icon: Search },
  { id: "analysis", icon: Sparkles },
  { id: "template", icon: Palette },
  { id: "ftp", icon: Server },
  { id: "generate", icon: Code },
  { id: "preview", icon: Eye },
];

export default function HtmlGenerator() {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [activeTab, setActiveTab] = useState("wizard");

  // Formularz
  const [domain, setDomain] = useState("");
  const [aiModel, setAiModel] = useState("gpt");
  const [analysis, setAnalysis] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("blog");
  const [customTitle, setCustomTitle] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [ftpHost, setFtpHost] = useState("");
  const [ftpUser, setFtpUser] = useState("");
  const [ftpPass, setFtpPass] = useState("");
  const [ftpTested, setFtpTested] = useState(null);
  const [generateResult, setGenerateResult] = useState(null);
  const [generatedFiles, setGeneratedFiles] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sites/html/history`);
      setHistory(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Krok 1: Analiza domeny
  const handleAnalyze = async () => {
    if (!domain.trim()) { toast.error(t("htmlGen.enterDomain")); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sites/html/analyze`, {
        domain: domain.trim(), ai_model: aiModel
      });
      setAnalysis(res.data);
      setCustomTitle(res.data.analysis?.title || "");
      setCustomDesc(res.data.analysis?.description || "");
      setSelectedTemplate(res.data.analysis?.recommended_template || "blog");
      setStep(1);
      toast.success(t("htmlGen.analysisComplete"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  // Krok 4: Test FTP
  const handleFtpTest = async () => {
    if (!ftpHost.trim()) { toast.error(t("htmlGen.enterFtpHost")); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sites/html/ftp-test`, {
        host: ftpHost, user: ftpUser, password: ftpPass
      });
      setFtpTested(res.data.success);
      toast[res.data.success ? "success" : "error"](res.data.message);
    } catch (err) {
      setFtpTested(false);
      toast.error(t("common.error"));
    } finally { setLoading(false); }
  };

  // Krok 5: Generuj strone
  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sites/html/generate`, {
        domain, template: selectedTemplate,
        analysis: analysis?.analysis || {},
        ai_model: aiModel,
        custom_title: customTitle || null,
        custom_description: customDesc || null,
        ftp_host: ftpHost || null,
        ftp_user: ftpUser || null,
        ftp_pass: ftpPass || null,
      });
      setGenerateResult(res.data);
      // Pobierz pliki
      const filesRes = await axios.get(`${API}/sites/html/preview-data/${res.data.gen_id}`);
      setGeneratedFiles(filesRes.data);
      setStep(5);
      fetchHistory();
      toast.success(t("htmlGen.generateSuccess"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  // Deploy FTP
  const handleDeploy = async () => {
    if (!generateResult || !ftpHost) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sites/html/deploy`, {
        gen_id: generateResult.gen_id,
        ftp_host: ftpHost, ftp_user: ftpUser, ftp_pass: ftpPass
      });
      toast[res.data.success ? "success" : "error"](res.data.message);
      if (res.data.success) fetchHistory();
    } catch (err) {
      toast.error(t("common.error"));
    } finally { setLoading(false); }
  };

  // Preview
  const handlePreview = async (genId) => {
    try {
      const res = await axios.get(`${API}/sites/html/preview-data/${genId}`);
      setPreviewHtml(res.data.index_html || "");
      setPreviewOpen(true);
    } catch (err) { toast.error(t("common.error")); }
  };

  // Delete
  const handleDeleteGen = async (genId) => {
    try {
      await axios.delete(`${API}/sites/html/history/${genId}`);
      toast.success(t("common.success"));
      fetchHistory();
    } catch (err) { toast.error(t("common.error")); }
  };

  const resetWizard = () => {
    setStep(0); setAnalysis(null); setGenerateResult(null);
    setGeneratedFiles(null); setFtpTested(null);
    setDomain(""); setCustomTitle(""); setCustomDesc("");
    setFtpHost(""); setFtpUser(""); setFtpPass("");
  };

  const stepLabels = [
    t("htmlGen.stepDomain"), t("htmlGen.stepAnalysis"), t("htmlGen.stepTemplate"),
    t("htmlGen.stepFtp"), t("htmlGen.stepGenerate"), t("htmlGen.stepPreview"),
  ];

  return (
    <div data-testid="html-generator-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">
            {t("htmlGen.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("htmlGen.subtitle")}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="html-gen-tabs">
          <TabsTrigger value="wizard" className="gap-1.5">
            <Code className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("htmlGen.wizard")}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5">
            <History className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("htmlGen.history")}
            {history.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] rounded-md">{history.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ===== WIZARD TAB ===== */}
        <TabsContent value="wizard" className="mt-4">
          {/* Stepper */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={s.id} className="flex items-center">
                  <button
                    data-testid={`step-${s.id}`}
                    onClick={() => i <= step && setStep(i)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      isActive ? "bg-primary text-primary-foreground shadow-sm" :
                      isDone ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                      "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isDone ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> :
                     <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    <span className="hidden sm:inline">{stepLabels[i]}</span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <ChevronRight className="w-3 h-3 mx-1 text-muted-foreground/40 shrink-0" />
                  )}
                </div>
              );
            })}
          </div>

          {/* ===== KROK 0: Domena ===== */}
          {step === 0 && (
            <Card data-testid="step-domain-card" className="rounded-xl border shadow-sm bg-card max-w-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Search className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  {t("htmlGen.stepDomain")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label>{t("htmlGen.domainLabel")}</Label>
                  <Input
                    data-testid="domain-input"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.pl"
                    className="text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("htmlGen.aiModelLabel")}</Label>
                  <Tabs value={aiModel} onValueChange={setAiModel}>
                    <TabsList className="grid grid-cols-3 w-full" data-testid="ai-model-tabs">
                      <TabsTrigger value="gpt">GPT-5.2</TabsTrigger>
                      <TabsTrigger value="claude">Claude</TabsTrigger>
                      <TabsTrigger value="gemini">Gemini</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <Button
                  data-testid="analyze-btn"
                  className="w-full h-11 rounded-lg gap-2"
                  onClick={handleAnalyze}
                  disabled={loading || !domain.trim()}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                   <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                  {loading ? t("htmlGen.analyzing") : t("htmlGen.analyzeDomain")}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* ===== KROK 1: Wynik analizy ===== */}
          {step === 1 && analysis && (
            <Card data-testid="step-analysis-card" className="rounded-xl border shadow-sm bg-card max-w-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  {t("htmlGen.analysisResult")} — {analysis.domain}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{t("htmlGen.category")}</p>
                    <p className="text-sm font-medium mt-1 text-foreground">{analysis.analysis?.category}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{t("htmlGen.language")}</p>
                    <p className="text-sm font-medium mt-1 text-foreground">{analysis.analysis?.language?.toUpperCase()}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("htmlGen.siteTitle")}</Label>
                  <Input data-testid="custom-title" value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t("htmlGen.siteDesc")}</Label>
                  <Textarea data-testid="custom-desc" value={customDesc}
                    onChange={(e) => setCustomDesc(e.target.value)} rows={2} className="resize-none" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t("htmlGen.keywords")}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {(analysis.analysis?.keywords || []).map((kw, i) => (
                      <Badge key={i} variant="secondary" className="rounded-md text-xs">{kw}</Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t("htmlGen.suggestedArticles")}</Label>
                  <div className="space-y-1">
                    {(analysis.analysis?.suggested_articles || []).map((art, i) => (
                      <p key={i} className="text-xs text-muted-foreground pl-3 border-l-2 border-primary/30 py-0.5">
                        {art}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(0)} className="gap-1">
                    <ChevronLeft className="w-3.5 h-3.5" /> {t("htmlGen.back")}
                  </Button>
                  <Button data-testid="next-to-template" onClick={() => setStep(2)} className="gap-1 flex-1">
                    {t("htmlGen.nextTemplate")} <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== KROK 2: Wybor szablonu ===== */}
          {step === 2 && (
            <Card data-testid="step-template-card" className="rounded-xl border shadow-sm bg-card max-w-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Palette className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  {t("htmlGen.selectTemplate")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  {(analysis?.templates || [
                    { id: "blog", name: "Blog / Magazine", description: "Szablon blogowy", features: ["artykuly", "kategorie"] },
                    { id: "business", name: "Business / Corporate", description: "Strona firmowa", features: ["uslugi", "kontakt"] },
                    { id: "portfolio", name: "Portfolio / Showcase", description: "Portfolio", features: ["galeria", "projekty"] },
                    { id: "news", name: "News / Portal", description: "Portal informacyjny", features: ["wiadomosci", "kategorie"] },
                  ]).map((tmpl) => (
                    <button
                      key={tmpl.id}
                      data-testid={`template-${tmpl.id}`}
                      onClick={() => setSelectedTemplate(tmpl.id)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        selectedTemplate === tmpl.id
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border hover:border-primary/30"
                      }`}
                    >
                      <p className="text-sm font-semibold text-foreground">{tmpl.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{tmpl.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {tmpl.features.map((f, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] rounded-md">{f}</Badge>
                        ))}
                      </div>
                      {selectedTemplate === tmpl.id && (
                        <div className="flex items-center gap-1 mt-2 text-primary text-xs font-medium">
                          <Check className="w-3 h-3" /> {t("htmlGen.selected")}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="gap-1">
                    <ChevronLeft className="w-3.5 h-3.5" /> {t("htmlGen.back")}
                  </Button>
                  <Button data-testid="next-to-ftp" onClick={() => setStep(3)} className="gap-1 flex-1">
                    {t("htmlGen.nextFtp")} <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== KROK 3: FTP Config ===== */}
          {step === 3 && (
            <Card data-testid="step-ftp-card" className="rounded-xl border shadow-sm bg-card max-w-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Server className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  {t("htmlGen.ftpConfig")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <p className="text-xs text-muted-foreground">{t("htmlGen.ftpOptional")}</p>
                <div className="grid gap-3">
                  <div className="space-y-2">
                    <Label>{t("htmlGen.ftpHost")}</Label>
                    <Input data-testid="ftp-host-input" value={ftpHost}
                      onChange={(e) => { setFtpHost(e.target.value); setFtpTested(null); }}
                      placeholder="ftp.example.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t("htmlGen.ftpUser")}</Label>
                      <Input data-testid="ftp-user-input" value={ftpUser}
                        onChange={(e) => setFtpUser(e.target.value)} placeholder="user" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("htmlGen.ftpPass")}</Label>
                      <Input data-testid="ftp-pass-input" type="password" value={ftpPass}
                        onChange={(e) => setFtpPass(e.target.value)} placeholder="*****" />
                    </div>
                  </div>
                </div>
                {ftpHost && (
                  <Button variant="outline" data-testid="ftp-test-btn" onClick={handleFtpTest}
                    disabled={loading} className="gap-2">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                     <Server className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    {t("htmlGen.testFtp")}
                    {ftpTested === true && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                    {ftpTested === false && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                  </Button>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="gap-1">
                    <ChevronLeft className="w-3.5 h-3.5" /> {t("htmlGen.back")}
                  </Button>
                  <Button data-testid="next-to-generate" onClick={() => setStep(4)} className="gap-1 flex-1">
                    {t("htmlGen.nextGenerate")} <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== KROK 4: Podsumowanie i generowanie ===== */}
          {step === 4 && (
            <Card data-testid="step-generate-card" className="rounded-xl border shadow-sm bg-card max-w-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Code className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  {t("htmlGen.generateSummary")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: t("htmlGen.domainLabel"), value: domain },
                    { label: t("htmlGen.template"), value: selectedTemplate },
                    { label: t("htmlGen.aiModelLabel"), value: aiModel.toUpperCase() },
                    { label: t("htmlGen.language"), value: analysis?.analysis?.language?.toUpperCase() || "PL" },
                  ].map((item, i) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{item.label}</p>
                      <p className="text-sm font-medium mt-1 text-foreground">{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{t("htmlGen.siteTitle")}</p>
                  <p className="text-sm font-medium mt-1 text-foreground">{customTitle || domain}</p>
                </div>
                {ftpHost && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">FTP</p>
                    <p className="text-sm font-medium mt-1 text-foreground">{ftpHost}</p>
                  </div>
                )}
                <Separator />
                <p className="text-xs text-muted-foreground">{t("htmlGen.generateWillCreate")}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><FileCode className="w-3.5 h-3.5" /> index.html</span>
                  <span className="flex items-center gap-1"><FileJson className="w-3.5 h-3.5" /> articles.json</span>
                  <span className="flex items-center gap-1"><FileCode className="w-3.5 h-3.5" /> api.php</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(3)} className="gap-1">
                    <ChevronLeft className="w-3.5 h-3.5" /> {t("htmlGen.back")}
                  </Button>
                  <Button data-testid="generate-html-btn" onClick={handleGenerate}
                    disabled={loading} className="gap-2 flex-1 h-11">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                     <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                    {loading ? t("htmlGen.generating") : t("htmlGen.generateNow")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== KROK 5: Podglad wygenerowanej strony ===== */}
          {step === 5 && generateResult && (
            <div className="space-y-4">
              <Card data-testid="step-preview-card" className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Eye className="w-4 h-4 text-primary" strokeWidth={1.5} />
                      {t("htmlGen.previewTitle")} — {generateResult.domain}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] rounded-md">{generateResult.template}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] border-0 rounded-md">
                        {t("htmlGen.generated")}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2 space-y-4">
                  {/* Info o plikach */}
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(generateResult.file_sizes || {}).map(([name, size]) => (
                      <div key={name} className="p-3 rounded-lg bg-muted/50 flex items-center gap-3">
                        <FileCode className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                        <div>
                          <p className="text-xs font-medium text-foreground">{name}</p>
                          <p className="text-[10px] text-muted-foreground">{(size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Preview iframe */}
                  {generatedFiles?.index_html && (
                    <div className="border rounded-lg overflow-hidden bg-white">
                      <iframe
                        data-testid="preview-iframe"
                        srcDoc={generatedFiles.index_html}
                        title="Preview"
                        className="w-full h-[400px] border-0"
                        sandbox="allow-scripts"
                      />
                    </div>
                  )}

                  {/* Tabs z plikami */}
                  {generatedFiles && (
                    <Tabs defaultValue="html" className="mt-4">
                      <TabsList data-testid="file-tabs">
                        <TabsTrigger value="html">index.html</TabsTrigger>
                        <TabsTrigger value="articles">articles.json</TabsTrigger>
                        <TabsTrigger value="api">api.php</TabsTrigger>
                      </TabsList>
                      <TabsContent value="html">
                        <ScrollArea className="h-[200px] rounded-lg border bg-muted/30 p-3">
                          <pre className="text-[11px] font-code text-foreground/80 whitespace-pre-wrap">
                            {generatedFiles.index_html?.substring(0, 3000)}
                            {(generatedFiles.index_html?.length || 0) > 3000 && "\n\n... (skrocono)"}
                          </pre>
                        </ScrollArea>
                      </TabsContent>
                      <TabsContent value="articles">
                        <ScrollArea className="h-[200px] rounded-lg border bg-muted/30 p-3">
                          <pre className="text-[11px] font-code text-foreground/80 whitespace-pre-wrap">
                            {JSON.stringify(generatedFiles.articles_json, null, 2)}
                          </pre>
                        </ScrollArea>
                      </TabsContent>
                      <TabsContent value="api">
                        <ScrollArea className="h-[200px] rounded-lg border bg-muted/30 p-3">
                          <pre className="text-[11px] font-code text-foreground/80 whitespace-pre-wrap">
                            {generatedFiles.api_php}
                          </pre>
                        </ScrollArea>
                      </TabsContent>
                    </Tabs>
                  )}

                  {/* Akcje */}
                  <div className="flex items-center gap-2 pt-2">
                    {ftpHost && (
                      <Button data-testid="deploy-btn" onClick={handleDeploy}
                        disabled={loading} className="gap-2 rounded-lg">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                         <Upload className="w-4 h-4" strokeWidth={1.5} />}
                        {t("htmlGen.deployFtp")}
                      </Button>
                    )}
                    <Button variant="outline" data-testid="new-generation-btn"
                      onClick={resetWizard} className="gap-2 rounded-lg">
                      <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {t("htmlGen.newGeneration")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ===== HISTORY TAB ===== */}
        <TabsContent value="history" className="mt-4">
          <Card data-testid="history-card" className="rounded-xl border shadow-sm bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <History className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                {t("htmlGen.history")}
                <Badge variant="secondary" className="text-[10px] rounded-md ml-auto">
                  {t("htmlGen.maxVersions")}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {history.length === 0 ? (
                <div data-testid="no-history" className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FolderOpen className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("htmlGen.noHistory")}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">{t("htmlGen.domainLabel")}</TableHead>
                      <TableHead className="text-xs font-semibold">{t("htmlGen.template")}</TableHead>
                      <TableHead className="text-xs font-semibold">{t("htmlGen.siteTitle")}</TableHead>
                      <TableHead className="text-xs font-semibold">AI</TableHead>
                      <TableHead className="text-xs font-semibold">Status</TableHead>
                      <TableHead className="text-xs font-semibold">{t("htmlGen.date")}</TableHead>
                      <TableHead className="text-xs font-semibold text-right">{t("sites.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((gen) => (
                      <TableRow key={gen.id} data-testid={`history-row-${gen.id}`}>
                        <TableCell className="text-xs font-medium">{gen.domain}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] rounded-md">{gen.template}</Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">{gen.title}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-[10px] rounded-md">{gen.ai_model?.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] border-0 rounded-md ${
                            gen.deployed
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          }`}>
                            {gen.deployed ? "Deployed" : "Local"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(gen.generated_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              data-testid={`preview-gen-${gen.id}`}
                              onClick={() => handlePreview(gen.id)}>
                              <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                              data-testid={`delete-gen-${gen.id}`}
                              onClick={() => handleDeleteGen(gen.id)}>
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-none h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("htmlGen.previewTitle")}</DialogTitle>
          </DialogHeader>
          <iframe
            srcDoc={previewHtml}
            title="Preview"
            className="w-full flex-1 border rounded-lg bg-white"
            style={{ height: "calc(80vh - 80px)" }}
            sandbox="allow-scripts"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
